import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Pencil, Plus, RefreshCw, Trash2, X, Check, AlertCircle, CheckCircle2, Search } from 'lucide-react';
import styles from './Attendance.module.css';
import { useStore } from '../../context/StoreContext';
import { SMIIFY_API } from '../../config/api';

const API = `${SMIIFY_API}/attendance`;
const WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const STATUS = {
    PRESENT: { label: 'Present', short: 'P', tone: 'present' },
    WORKING: { label: 'Working now', short: 'IN', tone: 'working' },
    HALF_DAY: { label: 'Half day', short: 'HD', tone: 'half' },
    ABSENT: { label: 'Absent', short: 'A', tone: 'absent' },
    LEAVE: { label: 'Leave', short: 'L', tone: 'leave' },
    WEEK_OFF: { label: 'Week off', short: 'WO', tone: 'off' },
    HOLIDAY: { label: 'Holiday', short: 'H', tone: 'holiday' },
    NOT_MARKED: { label: 'Not marked', short: '-', tone: 'pending' },
    UPCOMING: { label: '', short: '', tone: 'future' },
    NOT_JOINED: { label: 'Not joined', short: '', tone: 'future' },
};
const LEAVE_LABEL = { PAID: 'Paid', UNPAID: 'Unpaid', SICK: 'Sick', CASUAL: 'Casual' };

const localYmd = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const shiftDay = (ymd, n) => { const [y, m, d] = ymd.split('-').map(Number); return localYmd(new Date(y, m - 1, d + n)); };
const shiftMonth = (ym, n) => { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const to12 = (t) => { if (!t) return '—'; const [h, m] = t.split(':').map(Number); return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; };
const hm = (mins) => { const m = Math.max(0, Math.round(mins || 0)); return m ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : '—'; };
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const prettyDate = (ymd) => { const [y, m, d] = ymd.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }); };
const prettyMonth = (ym) => { const [y, m] = ym.split('-').map(Number); return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }); };
const nowHHMM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

const Chip = ({ status, leaveType }) => {
    const s = STATUS[status] || STATUS.NOT_MARKED;
    if (!s.label) return null;
    return <span className={`${styles.chip} ${styles[`t_${s.tone}`] || ''}`}>{s.label}{status === 'LEAVE' && leaveType ? ` (${LEAVE_LABEL[leaveType] || leaveType})` : ''}</span>;
};

export default function Attendance() {
    const { activeStore } = useStore();
    const storeId = activeStore?.id || activeStore?._id;
    const token = localStorage.getItem('smiify_token');
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();

    const today = localYmd();
    const [view, setView] = useState(params.get('staff') ? 'month' : 'day');
    const [date, setDate] = useState(today);
    const [month, setMonth] = useState(today.slice(0, 7));
    const [staffId, setStaffId] = useState(params.get('staff') || '');
    const [selectedDay, setSelectedDay] = useState(today);
    const [query, setQuery] = useState('');

    const [day, setDay] = useState(null);
    const [monthData, setMonthData] = useState(null);
    const [lastAudit, setLastAudit] = useState([]);
    const [loadingDay, setLoadingDay] = useState(false);
    const [loadingMonth, setLoadingMonth] = useState(false);
    const [busy, setBusy] = useState('');
    const [msg, setMsg] = useState(null);

    const [editId, setEditId] = useState(null);
    const [entry, setEntry] = useState({ in: '', out: '', note: '' });
    const [adding, setAdding] = useState(false);

    const tz = new Date().getTimezoneOffset();
    const headers = useCallback((json) => ({
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${token}`,
        ...(storeId ? { 'x-store-id': String(storeId) } : {}),
    }), [token, storeId]);

    const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), type === 'ok' ? 3000 : 6000); };
    const call = async (url, opts = {}) => {
        const res = await fetch(url, { ...opts, headers: headers(Boolean(opts.body)) });
        const json = await res.json().catch(() => ({}));
        if (res.status === 404 && !json.message) throw new Error('Attendance is not on the server yet. Deploy the new backend files.');
        if (!res.ok || json.success === false) throw new Error(json.message || `Request failed (${res.status}).`);
        return json;
    };

    /* ----------------------------- loaders ----------------------------- */
    const loadDay = useCallback(async () => {
        if (!storeId || storeId === 'global_hq') return;
        setLoadingDay(true);
        try {
            const json = await call(`${API}/day?date=${date}&tzOffset=${tz}`);
            setDay(json.data);
        } catch (e) { flash('error', e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); }
        finally { setLoadingDay(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, date, tz, headers]);

    const loadMonth = useCallback(async () => {
        if (!storeId || !staffId) { setMonthData(null); return; }
        setLoadingMonth(true);
        try {
            const json = await call(`${API}/staff/${staffId}/month?month=${month}&tzOffset=${tz}`);
            setMonthData(json.data);
        } catch (e) { flash('error', e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); setMonthData(null); }
        finally { setLoadingMonth(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, staffId, month, tz, headers]);

    useEffect(() => { loadDay(); }, [loadDay]);
    useEffect(() => { if (view === 'month') loadMonth(); }, [view, loadMonth]);

    // Live: refresh the day board every minute while viewing today
    useEffect(() => {
        if (view !== 'day' || date !== today) return undefined;
        const id = setInterval(() => { if (document.visibilityState === 'visible') loadDay(); }, 60000);
        return () => clearInterval(id);
    }, [view, date, today, loadDay]);

    const openStaff = (id, ymd = today) => {
        setStaffId(id);
        setSelectedDay(ymd);
        setMonth(ymd.slice(0, 7));
        setView('month');
        setParams({ staff: id }, { replace: true });
        setEditId(null);
        setAdding(false);
    };

    /* ----------------------------- actions ----------------------------- */
    const afterChange = async (json, text) => {
        flash('ok', json.message || text);
        if (json.data?.audit) setLastAudit(json.data.audit);
        await Promise.all([loadDay(), view === 'month' ? loadMonth() : null]);
    };

    const punch = async (id) => {
        setBusy(`punch-${id}`);
        try { await afterChange(await call(`${API}/punch`, { method: 'POST', body: JSON.stringify({ staffId: id, tzOffset: tz, source: 'WEB' }) }), 'Saved.'); }
        catch (e) { flash('error', e.message); }
        finally { setBusy(''); }
    };

    const saveEntry = async () => {
        if (!entry.in) { flash('error', 'IN time is required.'); return; }
        setBusy('entry');
        try {
            const url = editId ? `${API}/staff/${staffId}/${selectedDay}/punches/${editId}` : `${API}/staff/${staffId}/${selectedDay}/punches`;
            await afterChange(await call(url, { method: editId ? 'PUT' : 'POST', body: JSON.stringify({ ...entry, tzOffset: tz }) }), 'Saved.');
            setEditId(null); setAdding(false); setEntry({ in: '', out: '', note: '' });
        } catch (e) { flash('error', e.message); }
        finally { setBusy(''); }
    };

    const removeEntry = async (pid) => {
        if (!window.confirm('Delete this entry?')) return;
        setBusy(`del-${pid}`);
        try { await afterChange(await call(`${API}/staff/${staffId}/${selectedDay}/punches/${pid}?tzOffset=${tz}`, { method: 'DELETE' }), 'Deleted.'); }
        catch (e) { flash('error', e.message); }
        finally { setBusy(''); }
    };

    const setStatus = async (status, leaveType = '') => {
        setBusy(`status-${status}${leaveType}`);
        try { await afterChange(await call(`${API}/staff/${staffId}/${selectedDay}/status`, { method: 'PUT', body: JSON.stringify({ status, leaveType, tzOffset: tz }) }), 'Updated.'); }
        catch (e) { flash('error', e.message); }
        finally { setBusy(''); }
    };

    const approveMonthOt = async () => {
        if (!window.confirm(`Approve ${otPending} h overtime for ${prettyMonth(month)}? It will be paid in payroll.`)) return;
        setBusy('ot-month');
        try { await afterChange(await call(`${API}/staff/${staffId}/month/${month}/overtime`, { method: 'POST', body: JSON.stringify({ approved: true }) }), 'Approved.'); }
        catch (e) { flash('error', e.message); }
        finally { setBusy(''); }
    };

    const approveOt = async (approved) => {
        setBusy('ot');
        try { await afterChange(await call(`${API}/staff/${staffId}/${selectedDay}/status`, { method: 'PUT', body: JSON.stringify({ overtimeApproved: approved, tzOffset: tz }) }), 'Updated.'); }
        catch (e) { flash('error', e.message); }
        finally { setBusy(''); }
    };

    /* ----------------------------- derived ----------------------------- */
    const rows = useMemo(() => (day?.staff || []).filter((r) => !query || `${r.name} ${r.designation}`.toLowerCase().includes(query.toLowerCase())), [day, query]);
    const canManage = Boolean(day?.canManage ?? monthData?.canManage);
    const t = day?.totals || {};
    const selRaw = monthData?.days?.find((d) => d.date === selectedDay);
    const sel = selRaw && selRaw.status !== 'NOT_JOINED' ? { ...selRaw, punches: selRaw.punches || [] } : null;
    const otPending = monthData?.totals?.overtimePendingHours || 0;
    const staffRow = (day?.staff || []).find((r) => r.staffId === staffId);
    const isFutureSel = selectedDay > today;

    const calendar = useMemo(() => {
        if (!monthData?.days?.length) return [];
        const [y, m] = monthData.days[0].date.split('-').map(Number);
        const lead = new Date(y, m - 1, 1).getDay(); // 0 = Sunday, from the date itself
        return [...Array(lead).fill(null), ...monthData.days];
    }, [monthData]);

    if (!storeId || storeId === 'global_hq') {
        return <div className={styles.empty}>Select a branch from the top menu to see attendance.</div>;
    }

    /* ============================== render ============================== */
    return (
        <div className={styles.page}>
            {/* ---------------- header ---------------- */}
            <header className={styles.header}>
                <div className={styles.titleRow}>
                    <h1>Attendance</h1>
                    <div className={styles.segment} role="tablist">
                        <button type="button" role="tab" aria-selected={view === 'day'} className={view === 'day' ? styles.segOn : ''} onClick={() => { setView('day'); setParams({}, { replace: true }); }}>Day board</button>
                        <button type="button" role="tab" aria-selected={view === 'month'} className={view === 'month' ? styles.segOn : ''} onClick={() => setView('month')} disabled={!staffId && !(day?.staff?.length)}>Staff month</button>
                    </div>
                </div>
                <div className={styles.controls}>
                    {view === 'day' ? (
                        <>
                            <button type="button" className={styles.iconBtn} onClick={() => setDate(shiftDay(date, -1))} aria-label="Previous day"><ChevronLeft size={14} /></button>
                            <input type="date" className={styles.dateInput} value={date} max={today} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Date" />
                            <button type="button" className={styles.iconBtn} onClick={() => setDate(shiftDay(date, 1))} disabled={date >= today} aria-label="Next day"><ChevronRight size={14} /></button>
                            {date !== today && <button type="button" className={styles.btnGhost} onClick={() => setDate(today)}>Today</button>}
                        </>
                    ) : (
                        <>
                            <select className={styles.select} value={staffId} onChange={(e) => openStaff(e.target.value, `${month}-01` > today ? today : selectedDay)} aria-label="Staff">
                                <option value="" disabled>Choose staff</option>
                                {(day?.staff || []).map((r) => <option key={r.staffId} value={r.staffId}>{r.name}</option>)}
                            </select>
                            <button type="button" className={styles.iconBtn} onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Previous month"><ChevronLeft size={14} /></button>
                            <span className={styles.monthLabel}>{prettyMonth(month)}</span>
                            <button type="button" className={styles.iconBtn} onClick={() => setMonth(shiftMonth(month, 1))} aria-label="Next month"><ChevronRight size={14} /></button>
                        </>
                    )}
                    <button type="button" className={styles.iconBtn} onClick={() => { loadDay(); if (view === 'month') loadMonth(); }} aria-label="Reload"><RefreshCw size={13} className={loadingDay || loadingMonth ? styles.spin : ''} /></button>
                </div>
            </header>

            {msg && <div className={`${styles.msg} ${msg.type === 'ok' ? styles.msgOk : styles.msgErr}`} role="status">{msg.type === 'ok' ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />} {msg.text}</div>}

            {/* ================= DAY BOARD ================= */}
            {view === 'day' && (
                <div className={styles.content}>
                    <div className={styles.stats}>
                        {[
                            ['Present', (t.PRESENT || 0), 'present'], ['Working now', t.WORKING || 0, 'working'], ['Late', t.late || 0, 'absent'],
                            ['Half day', t.HALF_DAY || 0, 'half'], ['Not marked', t.NOT_MARKED || 0, 'pending'], ['Leave', t.LEAVE || 0, 'leave'],
                            ['Week off', t.WEEK_OFF || 0, 'off'], ['Absent', t.ABSENT || 0, 'absent'],
                        ].map(([label, value, tone]) => (
                            <div key={label} className={styles.stat}><i className={styles[`dot_${tone}`]} /><span>{label}</span><b>{value}</b></div>
                        ))}
                    </div>

                    <section className={styles.card}>
                        <div className={styles.cardHead}>
                            <h3>{prettyDate(date)}</h3>
                            <label className={styles.search}><Search size={12} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search staff" aria-label="Search staff" /></label>
                        </div>
                        {loadingDay && !day ? (
                            <div className={styles.loading}><Loader2 size={18} className={styles.spin} /> Loading...</div>
                        ) : rows.length === 0 ? (
                            <p className={styles.emptyText}>No active staff in this branch.</p>
                        ) : (
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr><th>Staff</th><th>Shift</th><th>Status</th><th>In</th><th>Out</th><th className={styles.r}>Worked</th><th>Flags</th><th className={styles.r}>Action</th></tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => {
                                            const c = r.computed;
                                            const canPunch = date === today && (canManage || r.isSelf) && !['LEAVE', 'WEEK_OFF', 'HOLIDAY'].includes(r.override ? r.status : '');
                                            return (
                                                <tr key={r.staffId} className={styles.clickRow} onClick={() => openStaff(r.staffId, date)}>
                                                    <td><strong>{r.name}</strong><small>{r.designation || 'Staff'}</small></td>
                                                    <td className={styles.muted}>{to12(r.shiftStart)} to {to12(r.shiftEnd)}</td>
                                                    <td><Chip status={r.status} leaveType={r.leaveType} /></td>
                                                    <td>{c?.firstIn ? to12(c.firstIn) : '—'}</td>
                                                    <td>{c?.isOpen ? <span className={styles.live}>Inside</span> : c?.lastOut ? to12(c.lastOut) : '—'}</td>
                                                    <td className={styles.r}>{hm(c?.workedMinutes)}</td>
                                                    <td>
                                                        {c?.isLate && <span className={styles.flagBad}>Late {c.lateMinutes}m</span>}
                                                        {c?.isEarlyExit && <span className={styles.flagBad}>Early {c.earlyExitMinutes}m</span>}
                                                        {c?.overtimeMinutes > 0 && <span className={styles.flagGood}>OT {hm(c.overtimeMinutes)}</span>}
                                                    </td>
                                                    <td className={styles.r} onClick={(e) => e.stopPropagation()}>
                                                        {canPunch && (
                                                            <button type="button" className={c?.isOpen ? styles.btnOut : styles.btnIn} onClick={() => punch(r.staffId)} disabled={busy === `punch-${r.staffId}`}>
                                                                {busy === `punch-${r.staffId}` ? <Loader2 size={12} className={styles.spin} /> : c?.isOpen ? 'Punch out' : 'Punch in'}
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </div>
            )}

            {/* ================= STAFF MONTH ================= */}
            {view === 'month' && (
                <div className={styles.content}>
                    {!staffId ? (
                        <p className={styles.emptyText}>Choose a staff member above.</p>
                    ) : loadingMonth && !monthData ? (
                        <div className={styles.loading}><Loader2 size={18} className={styles.spin} /> Loading month...</div>
                    ) : monthData && (
                        <>
                            <div className={styles.profileBar}>
                                <div>
                                    <strong>{monthData.name}</strong>
                                    <small>Shift {to12(monthData.rules.shiftStart)} to {to12(monthData.rules.shiftEnd)}, late after {monthData.rules.lateGrace} min, {monthData.rules.lateAllowed} free late marks</small>
                                </div>
                                <button type="button" className={styles.btnGhost} onClick={() => navigate(`/staff/${staffId}`)}>Staff rules</button>
                            </div>

                            <div className={styles.stats}>
                                {[
                                    ['Present', monthData.totals.present, 'present'], ['Half day', monthData.totals.halfDay, 'half'], ['Absent', monthData.totals.absent, 'absent'],
                                    ['Leave', monthData.totals.paidLeave + monthData.totals.unpaidLeave + monthData.totals.sickLeave + monthData.totals.casualLeave, 'leave'],
                                    ['Week off', monthData.totals.weekOff, 'off'], ['Late', `${monthData.totals.late}/${monthData.rules.lateAllowed}`, 'absent'],
                                    ['Early exit', `${monthData.totals.earlyExit}/${monthData.rules.earlyAllowed}`, 'half'],
                                    ['Overtime', otPending > 0 ? `${monthData.totals.overtimeApprovedHours}/${monthData.totals.overtimeHours} h approved` : `${monthData.totals.overtimeHours} h`, 'present'],
                                    ['Worked', `${monthData.totals.workedHours} h`, 'working'],
                                ].map(([label, value, tone]) => (
                                    <div key={label} className={styles.stat}><i className={styles[`dot_${tone}`]} /><span>{label}</span><b>{value}</b></div>
                                ))}
                            </div>

                            {otPending > 0 && (
                                <div className={styles.otBanner}>
                                    <span><b>{otPending} h overtime</b> is waiting for approval{monthData.salary?.overtimePendingPay ? ` (about ${inr(monthData.salary.overtimePendingPay)})` : ''}. It is paid only after approval.</span>
                                    {canManage && (
                                        <button type="button" className={styles.btnIn} onClick={approveMonthOt} disabled={busy === 'ot-month'}>
                                            {busy === 'ot-month' ? <Loader2 size={12} className={styles.spin} /> : 'Approve all overtime'}
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className={styles.monthGrid}>
                                {/* calendar */}
                                <section className={styles.card}>
                                    <div className={styles.calHead}>{WEEK.map((w) => <span key={w}>{w}</span>)}</div>
                                    <div className={styles.cal}>
                                        {calendar.map((d, i) => (d ? (
                                            <button
                                                type="button"
                                                key={d.date}
                                                disabled={d.status === 'NOT_JOINED'}
                                                title={d.status === 'NOT_JOINED' ? 'Before joining' : undefined}
                                                className={`${styles.cell} ${styles[`c_${(STATUS[d.status] || STATUS.NOT_MARKED).tone}`] || ''} ${d.status === 'NOT_JOINED' ? styles.cellOff : ''} ${d.date === selectedDay && d.status !== 'NOT_JOINED' ? styles.cellSel : ''} ${d.date === today ? styles.cellToday : ''}`}
                                                onClick={() => { if (d.status === 'NOT_JOINED') return; setSelectedDay(d.date); setEditId(null); setAdding(false); setLastAudit([]); }}
                                                aria-label={`${prettyDate(d.date)}: ${d.status === 'NOT_JOINED' ? 'before joining' : (STATUS[d.status] || {}).label || ''}`}
                                            >
                                                <span className={styles.cellNum}>{Number(d.date.slice(8))}</span>
                                                <span className={styles.cellCode}>{(STATUS[d.status] || {}).short}</span>
                                                <span className={styles.cellMarks}>
                                                    {d.computed?.isLate && <i className={styles.markLate} title="Late" />}
                                                    {d.computed?.overtimeMinutes > 0 && <i className={styles.markOt} title="Overtime" />}
                                                </span>
                                            </button>
                                        ) : <span key={`b${i}`} />))}
                                    </div>
                                    <div className={styles.legend}>
                                        {['PRESENT', 'HALF_DAY', 'ABSENT', 'LEAVE', 'WEEK_OFF', 'HOLIDAY'].map((k) => <span key={k}><i className={styles[`dot_${STATUS[k].tone}`]} /> {STATUS[k].label}</span>)}
                                        <span><i className={styles.markLate} /> Late</span><span><i className={styles.markOt} /> Overtime</span>
                                    </div>
                                </section>

                                {/* selected day */}
                                <section className={styles.card}>
                                    {!sel && (
                                        <div className={styles.pickDay}>
                                            <strong>{selRaw?.status === 'NOT_JOINED' ? 'Before joining date' : 'Pick a day'}</strong>
                                            <span>{selRaw?.status === 'NOT_JOINED' ? 'This staff member had not joined yet on this day.' : 'Click a day in the calendar to see and edit its entries.'}</span>
                                        </div>
                                    )}
                                    {sel && (
                                        <>
                                            <div className={styles.cardHead}>
                                                <h3>{prettyDate(sel.date)}</h3>
                                                <Chip status={sel.status} leaveType={sel.leaveType} />
                                            </div>

                                            {sel.computed && (
                                                <div className={styles.kvGrid}>
                                                    <div><span>In</span><b>{to12(sel.computed.firstIn)}</b></div>
                                                    <div><span>Out</span><b>{sel.computed.isOpen ? 'Inside' : to12(sel.computed.lastOut)}</b></div>
                                                    <div><span>Worked</span><b>{hm(sel.computed.workedMinutes)}</b></div>
                                                    <div><span>Break</span><b>{hm(sel.computed.breakMinutes)}</b></div>
                                                    <div><span>Late</span><b className={sel.computed.isLate ? styles.bad : ''}>{sel.computed.lateMinutes ? `${sel.computed.lateMinutes} min` : '—'}</b></div>
                                                    <div><span>Early exit</span><b className={sel.computed.isEarlyExit ? styles.bad : ''}>{sel.computed.earlyExitMinutes ? `${sel.computed.earlyExitMinutes} min` : '—'}</b></div>
                                                    <div><span>Overtime</span><b className={sel.computed.overtimeMinutes ? styles.good : ''}>{hm(sel.computed.overtimeMinutes)}</b></div>
                                                </div>
                                            )}

                                            {sel.computed?.overtimeMinutes > 0 && canManage && (
                                                <label className={styles.otRow}>
                                                    <input type="checkbox" checked={sel.overtimeApproved} onChange={(e) => approveOt(e.target.checked)} disabled={busy === 'ot'} />
                                                    Approve {hm(sel.computed.overtimeMinutes)} overtime for pay
                                                </label>
                                            )}

                                            {/* entries */}
                                            <h4 className={styles.subHead}>Entries</h4>
                                            {sel.punches.length === 0 && !adding && <p className={styles.emptyText}>No entries.</p>}
                                            <ul className={styles.punches}>
                                                {sel.punches.map((p) => (editId === String(p._id) ? (
                                                    <li key={p._id} className={styles.editRow}>
                                                        <input type="time" className={styles.timeInput} value={entry.in} onChange={(e) => setEntry({ ...entry, in: e.target.value })} aria-label="In" />
                                                        <input type="time" className={styles.timeInput} value={entry.out} onChange={(e) => setEntry({ ...entry, out: e.target.value })} aria-label="Out" />
                                                        <input className={styles.noteInput} value={entry.note} onChange={(e) => setEntry({ ...entry, note: e.target.value })} placeholder="Reason" aria-label="Reason" />
                                                        <button type="button" className={styles.iconBtn} onClick={saveEntry} disabled={busy === 'entry'} aria-label="Save"><Check size={13} /></button>
                                                        <button type="button" className={styles.iconBtn} onClick={() => setEditId(null)} aria-label="Cancel"><X size={13} /></button>
                                                    </li>
                                                ) : (
                                                    <li key={p._id}>
                                                        <span className={styles.punchTime}>{to12(p.in)} to {p.out ? to12(p.out) : <em className={styles.live}>inside</em>}</span>
                                                        <span className={styles.punchMeta}>
                                                            {p.source === 'MANUAL' ? 'Manual' : p.source === 'BIOMETRIC' ? 'Machine' : p.source === 'MOBILE' ? 'Mobile' : 'Web'}
                                                            {p.editedByName ? `, edited by ${p.editedByName}` : p.createdByName ? `, by ${p.createdByName}` : ''}
                                                            {p.note ? `. ${p.note}` : ''}
                                                        </span>
                                                        {canManage && (
                                                            <span className={styles.punchActions}>
                                                                <button type="button" className={styles.iconBtn} onClick={() => { setEditId(String(p._id)); setAdding(false); setEntry({ in: p.in, out: p.out || '', note: p.note || '' }); }} aria-label="Edit entry"><Pencil size={12} /></button>
                                                                <button type="button" className={styles.iconBtn} onClick={() => removeEntry(p._id)} disabled={busy === `del-${p._id}`} aria-label="Delete entry"><Trash2 size={12} /></button>
                                                            </span>
                                                        )}
                                                    </li>
                                                )))}
                                                {adding && (
                                                    <li className={styles.editRow}>
                                                        <input type="time" className={styles.timeInput} value={entry.in} onChange={(e) => setEntry({ ...entry, in: e.target.value })} aria-label="In" />
                                                        <input type="time" className={styles.timeInput} value={entry.out} onChange={(e) => setEntry({ ...entry, out: e.target.value })} aria-label="Out" />
                                                        <input className={styles.noteInput} value={entry.note} onChange={(e) => setEntry({ ...entry, note: e.target.value })} placeholder="Reason (missed punch...)" aria-label="Reason" />
                                                        <button type="button" className={styles.iconBtn} onClick={saveEntry} disabled={busy === 'entry'} aria-label="Save"><Check size={13} /></button>
                                                        <button type="button" className={styles.iconBtn} onClick={() => setAdding(false)} aria-label="Cancel"><X size={13} /></button>
                                                    </li>
                                                )}
                                            </ul>

                                            <div className={styles.actions}>
                                                {selectedDay === today && (canManage || staffRow?.isSelf) && (
                                                    <button type="button" className={staffRow?.computed?.isOpen ? styles.btnOut : styles.btnIn} onClick={() => punch(staffId)} disabled={busy === `punch-${staffId}`}>
                                                        {staffRow?.computed?.isOpen ? 'Punch out now' : 'Punch in now'}
                                                    </button>
                                                )}
                                                {canManage && !isFutureSel && !adding && !editId && (
                                                    <button type="button" className={styles.btnGhost} onClick={() => { setAdding(true); setEntry({ in: selectedDay === today ? nowHHMM() : (monthData.rules.shiftStart || '10:00'), out: '', note: '' }); }}><Plus size={12} /> Add entry</button>
                                                )}
                                            </div>

                                            {/* status */}
                                            {canManage && (
                                                <>
                                                    <h4 className={styles.subHead}>Mark day as</h4>
                                                    <div className={styles.statusBtns}>
                                                        {[['LEAVE', 'PAID', 'Paid leave'], ['LEAVE', 'UNPAID', 'Unpaid leave'], ['LEAVE', 'SICK', 'Sick leave'], ['LEAVE', 'CASUAL', 'Casual leave'], ['WEEK_OFF', '', 'Week off'], ['HOLIDAY', '', 'Holiday']]
                                                            .concat(isFutureSel ? [] : [['PRESENT', '', 'Present'], ['HALF_DAY', '', 'Half day'], ['ABSENT', '', 'Absent']])
                                                            .map(([s, lt, label]) => {
                                                                const on = sel.override && sel.status === s && (s !== 'LEAVE' || sel.leaveType === lt);
                                                                return <button type="button" key={label} className={`${styles.sBtn} ${on ? styles.sBtnOn : ''}`} onClick={() => setStatus(s, lt)} disabled={busy.startsWith('status')}>{label}</button>;
                                                            })}
                                                        {sel.override && <button type="button" className={styles.sBtnClear} onClick={() => setStatus('')} disabled={busy.startsWith('status')}>Use punches (auto)</button>}
                                                    </div>
                                                </>
                                            )}

                                            {lastAudit.length > 0 && (
                                                <>
                                                    <h4 className={styles.subHead}>Changes</h4>
                                                    <ul className={styles.audit}>
                                                        {lastAudit.map((a, i) => <li key={i}><b>{a.byName || 'Someone'}</b>: {a.detail || a.action} <em>{new Date(a.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</em></li>)}
                                                    </ul>
                                                </>
                                            )}
                                        </>
                                    )}
                                </section>
                            </div>

                            {/* salary preview (admin / manager only) */}
                            {monthData.salary && (
                                <section className={styles.card}>
                                    <div className={styles.cardHead}><h3>Salary preview, {prettyMonth(month)}</h3><small className={styles.muted}>{monthData.salary.note}</small></div>
                                    <div className={styles.salary}>
                                        {monthData.salary.type === 'MONTHLY' && <div><span>Monthly gross</span><b>{inr(monthData.salary.monthlyGross)}</b></div>}
                                        <div><span>One day</span><b>{inr(monthData.salary.perDay)}</b></div>
                                        <div className={styles.minus}><span>Unpaid days ({monthData.salary.unpaidDays})</span><b>- {inr(monthData.salary.unpaidAmount)}</b></div>
                                        {monthData.salary.lateDeduction > 0 && <div className={styles.minus}><span>Late beyond free ({monthData.salary.lateOverAllowance})</span><b>- {inr(monthData.salary.lateDeduction)}</b></div>}
                                        {monthData.salary.earlyDeduction > 0 && <div className={styles.minus}><span>Early exits beyond free ({monthData.salary.earlyOverAllowance})</span><b>- {inr(monthData.salary.earlyDeduction)}</b></div>}
                                        {monthData.salary.weekendPenalty > 0 && <div className={styles.minus}><span>Weekend leave penalty ({monthData.salary.weekendPenaltyDays} days)</span><b>- {inr(monthData.salary.weekendPenalty)}</b></div>}
                                        {monthData.salary.overtimePay > 0 && <div className={styles.plus}><span>Overtime ({monthData.salary.overtimeHoursPaid} h)</span><b>+ {inr(monthData.salary.overtimePay)}</b></div>}
                                        {monthData.salary.overtimePendingHours > 0 && <div className={styles.pending}><span>Overtime not approved ({monthData.salary.overtimePendingHours} h)</span><b>{inr(monthData.salary.overtimePendingPay)}</b></div>}
                                        <div className={styles.total}><span>Estimated pay</span><b>{inr(monthData.salary.netPreview)}</b></div>
                                    </div>
                                    {(monthData.salary.excessPaidLeaves > 0 || monthData.salary.excessWeekOffs > 0) && (
                                        <p className={styles.muted}>
                                            {monthData.salary.excessPaidLeaves > 0 && `${monthData.salary.excessPaidLeaves} leave(s) above the free limit counted as unpaid. `}
                                            {monthData.salary.excessWeekOffs > 0 && `${monthData.salary.excessWeekOffs} extra week off(s) counted as unpaid.`}
                                        </p>
                                    )}
                                </section>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}